import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Send, Hammer, Camera } from 'lucide-react';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import MonthGrid, { STEP_STYLE } from '@/components/calendar/MonthGrid';
import {
  fetchExecutionCalendar, fetchPostingCalendar, fetchShootsCalendar, fetchClients, fetchStrategies, qk,
} from '@/lib/queries';
import { cn } from '@/lib/cn';

type Tab = 'execution' | 'posting' | 'shoots';

export default function CalendarPage() {
  const [tab, setTab] = useState<Tab>('execution');
  const [monthStart, setMonthStart] = useState<Date>(() => firstOfMonth(new Date()));
  const [clientId, setClientId] = useState('');
  const [strategyId, setStrategyId] = useState('');

  const startISO = monthStart.toISOString().slice(0, 10);
  const endISO = lastOfMonth(monthStart).toISOString().slice(0, 10);

  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients });
  const strategies = useQuery({ queryKey: qk.strategies, queryFn: fetchStrategies });

  const events = useQuery({
    queryKey: ['calendar', tab, startISO, endISO, clientId, strategyId],
    queryFn: () => (tab === 'execution'
      ? fetchExecutionCalendar(startISO, endISO, { client_id: clientId, strategy_id: strategyId })
      : tab === 'posting'
        ? fetchPostingCalendar(startISO, endISO,   { client_id: clientId, strategy_id: strategyId })
        : fetchShootsCalendar(startISO, endISO, { client_id: clientId })),
  });

  const monthLabel = useMemo(
    () => monthStart.toLocaleDateString('en-AE', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    [monthStart]
  );

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle="When work happens (Execution) and when content goes live (Posting)."
      />

      {/* Tab toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setTab('execution')}
          className={cn(
            'inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition',
            tab === 'execution'
              ? 'border-accent-400 bg-accent-600/20 text-accent-400'
              : 'border-slate-800 text-muted hover:text-muted-strong'
          )}
        >
          <Hammer size={14} /> Execution
        </button>
        <button
          onClick={() => setTab('posting')}
          className={cn(
            'inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition',
            tab === 'posting'
              ? 'border-accent-400 bg-accent-600/20 text-accent-400'
              : 'border-slate-800 text-muted hover:text-muted-strong'
          )}
        >
          <Send size={14} /> Posting
        </button>
        <button
          onClick={() => setTab('shoots')}
          className={cn(
            'inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition',
            tab === 'shoots'
              ? 'border-accent-400 bg-accent-600/20 text-accent-400'
              : 'border-slate-800 text-muted hover:text-muted-strong'
          )}
        >
          <Camera size={14} /> Shoots
        </button>
        <span className="text-[11px] text-muted ml-2">
          {tab === 'execution'
            ? 'Shoots, edits, designs + tasks due'
            : tab === 'posting'
              ? 'When each creative goes live per channel'
              : 'Shoot days: time, place, crew and how many creatives'}
        </span>
      </div>

      {/* Filters */}
      <Card className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Client" value={clientId} onChange={setClientId} searchable
            options={[
              { value: '', label: 'All clients' },
              ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]} />
          <Select label="Strategy" value={strategyId} onChange={setStrategyId} searchable
            options={[
              { value: '', label: 'All strategies' },
              ...(strategies.data ?? []).map((s) => ({ value: s.id, label: s.title, hint: s.code })),
            ]} />
        </div>
      </Card>

      {/* Date navigation - flexible */}
      <Card className="mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <Select
              label="Month"
              value={String(monthStart.getUTCMonth())}
              onChange={(v) => setMonthStart(new Date(Date.UTC(monthStart.getUTCFullYear(), Number(v), 1)))}
              options={MONTHS.map((m, i) => ({ value: String(i), label: m }))}
            />
          </div>
          <div className="md:col-span-2">
            <Select
              label="Year"
              value={String(monthStart.getUTCFullYear())}
              onChange={(v) => setMonthStart(new Date(Date.UTC(Number(v), monthStart.getUTCMonth(), 1)))}
              options={yearRange(monthStart.getUTCFullYear()).map((y) => ({ value: String(y), label: String(y) }))}
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-muted mb-1.5">Jump to date</label>
            <input
              type="date"
              value={monthStart.toISOString().slice(0, 10)}
              onChange={(e) => {
                if (!e.target.value) return;
                const [y, m] = e.target.value.split('-').map(Number);
                setMonthStart(new Date(Date.UTC(y, m - 1, 1)));
              }}
              className="w-full h-10 rounded-lg border border-slate-800 bg-bg px-3 text-sm text-muted-strong focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40 [color-scheme:dark]"
            />
          </div>
          <div className="md:col-span-4 flex items-center gap-2 justify-end">
            <Button variant="secondary" size="sm" icon={<ChevronLeft size={14} />}
              onClick={() => setMonthStart(addMonths(monthStart, -1))}>Prev</Button>
            <Button variant="ghost" size="sm" onClick={() => setMonthStart(firstOfMonth(new Date()))}>
              Today
            </Button>
            <Button variant="secondary" size="sm" icon={<ChevronRight size={14} />}
              onClick={() => setMonthStart(addMonths(monthStart, 1))}>Next</Button>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-800 pt-3 mt-3">
          <div className="text-lg font-semibold text-muted-strong inline-flex items-center gap-2">
            <CalIcon size={16} className="text-accent-400" /> {monthLabel}
          </div>
          <div className="text-xs text-muted">
            {events.isLoading ? 'Loading...' : `${events.data?.length ?? 0} events this month`}
          </div>
        </div>
      </Card>

      <MonthGrid monthStart={monthStart} events={events.data ?? []} />

      {/* Legend */}
      <Card className="mt-4">
        <div className="text-xs text-muted mb-2">Legend <span className="text-[10px] text-muted/70">the step each creative is on, in order</span></div>
        <div className="flex flex-wrap gap-2 text-[10px]">
          {STEP_STYLE.map((s) => <Legend key={s.key} color={s.color} label={s.label} />)}
        </div>
        <div className="text-[10px] text-muted mt-2">Tasks are coloured by their own status: pending, in progress, submitted, done, blocked.</div>
      </Card>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return <span className={cn('px-2 py-0.5 rounded-full', color)}>{label}</span>;
}

function firstOfMonth(d: Date) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)); }
function lastOfMonth(d: Date)  { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)); }
function addMonths(d: Date, n: number) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)); }

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// 5 years back, 5 years forward, plus the current focused year (in case it's outside)
function yearRange(focused: number): number[] {
  const thisYear = new Date().getUTCFullYear();
  const lo = Math.min(thisYear - 5, focused);
  const hi = Math.max(thisYear + 5, focused);
  const out: number[] = [];
  for (let y = lo; y <= hi; y++) out.push(y);
  return out;
}
